import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, CardHeader, CardBody, CardTitle, Row, Col, Badge, Spinner,
    Button, Table, Alert
} from 'reactstrap';
import { getClientDashboard } from './service/clientsService';
import toast, { Toaster } from "react-hot-toast";
import './ControlClientes.css';
import { useSede } from "context/SedeContext";
import useSedeRefresh from "../../../hooks/useSedeRefresh";

const ClientDashboard = ({ clientId, onBack }) => {
    const { addSedeParams, addSedeHeaders } = useSede();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadDashboard = useCallback(async () => {
        if (!clientId) return;
        
        try {
            setLoading(true);
            setError(null);
            const sedeParams = addSedeParams({});
            const sedeHeaders = addSedeHeaders({});
            const data = await getClientDashboard(clientId, sedeParams, sedeHeaders);
            setDashboardData(data);
        } catch (err) {
            console.error("Error al cargar dashboard:", err);
            setError('Error al cargar el dashboard del cliente');
            toast.error('Error al cargar el dashboard del cliente');
        } finally {
            setLoading(false);
        }
    }, [clientId, addSedeParams, addSedeHeaders]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    // Escuchar cambios de sede y refrescar automáticamente
    useSedeRefresh(loadDashboard, [addSedeParams, addSedeHeaders]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <Spinner color="primary" size="lg" />
                    <p className="mt-3 text-muted">Cargando dashboard del cliente...</p>
                </div>
            </div>
        );
    }

    if (error || !dashboardData) {
        return (
            <Alert color="danger">
                <h4 className="alert-heading">Error</h4>
                <p>{error || 'No se pudo cargar la información del cliente'}</p>
                <Button color="primary" onClick={loadDashboard}>
                    Reintentar
                </Button>
            </Alert>
        );
    }

    const { client, current_membership, statistics, recent_bookings, payment_history } = dashboardData;

    const getStatusIcon = (status) => {
        const icons = {
            "Activo": "fa-check-circle",
            "Nuevo": "fa-user-plus",
            "Renovación": "fa-exclamation-triangle",
            "Inactivo": "fa-times-circle"
        };
        return icons[status] || "fa-question-circle";
    };

    const getAttendanceStatusIcon = (status) => {
        const icons = {
            "attended": "fa-check text-success",
            "no_show": "fa-times text-danger",
            "cancelled": "fa-ban text-warning",
            "pending": "fa-clock text-secondary"
        };
        return icons[status] || "fa-question text-muted";
    };

    return (
        <>
            <Toaster position="top-right" />
            
            {/* Header */}
            <div className="dashboard-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button 
                            color="secondary" 
                            outline 
                            size="sm" 
                            onClick={onBack}
                            className="mr-3"
                        >
                            <i className="fa fa-arrow-left mr-1"></i>
                            Volver
                        </Button>
                        <div>
                            <h2 className="mb-1">
                                <i className="nc-icon nc-single-02 mr-2"></i>
                                {client.first_name} {client.last_name}
                            </h2>
                            <p className="text-muted mb-0">
                                <i className="fa fa-envelope mr-1"></i>
                                {client.email} • 
                                <i className="fa fa-phone ml-2 mr-1"></i>
                                {client.phone}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <Badge 
                            color={client.status_color} 
                            className="px-3 py-2 d-flex align-items-center"
                        >
                            <i className={`fa ${getStatusIcon(client.status)} mr-2`}></i>
                            {client.status}
                        </Badge>
                        <small className="text-muted d-block mt-1">
                            Cliente desde {client.created_at}
                        </small>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="stat-card">
                        <CardBody className="text-center">
                            <div className="stat-icon bg-primary">
                                <i className="fa fa-calendar-check"></i>
                            </div>
                            <h3 className="stat-number text-primary">{statistics.total_bookings}</h3>
                            <p className="stat-label">Total Reservas</p>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <CardBody className="text-center">
                            <div className="stat-icon bg-success">
                                <i className="fa fa-check"></i>
                            </div>
                            <h3 className="stat-number text-success">{statistics.attended_bookings}</h3>
                            <p className="stat-label">Asistencias</p>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <CardBody className="text-center">
                            <div className="stat-icon bg-warning">
                                <i className="fa fa-percentage"></i>
                            </div>
                            <h3 className="stat-number text-warning">{statistics.attendance_rate}%</h3>
                            <p className="stat-label">Tasa de Asistencia</p>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="stat-card">
                        <CardBody className="text-center">
                            <div className="stat-icon bg-info">
                                <i className="fa fa-clock"></i>
                            </div>
                            <h3 className="stat-number text-info">{statistics.remaining_classes}</h3>
                            <p className="stat-label">Clases Restantes</p>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Current Month & Membership Info */}
            <Row className="mb-4">
                <Col md={6}>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                <i className="fa fa-calendar mr-2"></i>
                                Este Mes
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col md={6}>
                                    <div className="text-center">
                                        <h4 className="text-success">{statistics.current_month_attended}</h4>
                                        <p className="text-muted mb-0">Clases Asistidas</p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="text-center">
                                        <h4 className="text-danger">{statistics.current_month_no_show}</h4>
                                        <p className="text-muted mb-0">Inasistencias</p>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                <i className="fa fa-id-card mr-2"></i>
                                Membresía Actual
                            </CardTitle>
                        </CardHeader>
                        <CardBody>
                            <div className="membership-info">
                                <h5 className="text-primary">{current_membership.name}</h5>
                                <p className="text-muted mb-1">
                                    <i className="fa fa-calendar mr-1"></i>
                                    Válida hasta: {current_membership.valid_until || 'N/A'}
                                </p>
                                <p className="text-muted mb-0">
                                    <i className="fa fa-dumbbell mr-1"></i>
                                    {current_membership.classes_per_month} clases por mes
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Recent Bookings & Payment History */}
            <Row>
                <Col md={8}>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                <i className="fa fa-history mr-2"></i>
                                Reservas Recientes
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="p-0">
                            <Table responsive className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Horario</th>
                                        <th>Estado</th>
                                        <th>Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent_bookings.map((booking) => (
                                        <tr key={booking.id}>
                                            <td>{booking.class_date}</td>
                                            <td>{booking.schedule}</td>
                                            <td>
                                                <span className="d-flex align-items-center">
                                                    <i className={`fa ${getAttendanceStatusIcon(booking.attendance_status)} mr-2`}></i>
                                                    {booking.attendance_status === 'attended' ? 'Asistió' :
                                                     booking.attendance_status === 'no_show' ? 'Inasistencia' :
                                                     booking.attendance_status === 'cancelled' ? 'Cancelada' : 'Pendiente'}
                                                </span>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {booking.cancellation_reason || '—'}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card>
                        <CardHeader>
                            <CardTitle tag="h5" className="mb-0">
                                <i className="fa fa-credit-card mr-2"></i>
                                Historial de Pagos
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="p-0">
                            <div className="payment-list">
                                {payment_history.map((payment) => (
                                    <div key={payment.id} className="payment-item">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="mb-1">{payment.membership_name}</h6>
                                                <small className="text-muted">
                                                    {payment.date_paid}
                                                </small>
                                            </div>
                                            <div className="text-right">
                                                <span className="font-weight-bold text-success">
                                                    Q{payment.amount}
                                                </span>
                                                {payment.is_active && (
                                                    <Badge color="success" size="sm" className="ml-2">
                                                        Activo
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <small className="text-muted">
                                            Válido hasta: {payment.valid_until}
                                        </small>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default ClientDashboard;
